import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gavel, Users, Timer, Mic, MessageSquare, Home, X, StickyNote, Vote } from 'lucide-react';
import QuorumStatus from '../components/common/QuorumStatus';
import SignedInUserLine from '../components/common/SignedInUserLine';
import { getQuorumStatus } from '../utils/quorumUtils';
import { getChairNextStep } from '../utils/getChairNextStep';
import { formatTime } from '../utils/timeUtils';
import {
  ELECTION_STAGES,
  getElectionOfficeLabel,
  getElectionWinners,
} from '../utils/electionChairScript';

const STAGE_SHORT = {
  pending_second: { i: 0, label: 'Made' },
  seconded: { i: 1, label: '2nd' },
  stated: { i: 2, label: 'Debate' },
  open_debate: { i: 2, label: 'Debate' },
  close_debate: { i: 3, label: 'Put' },
  ready_for_vote: { i: 3, label: 'Put' },
  voted_passed: { i: 4, label: 'Vote' },
  voted_failed: { i: 4, label: 'Vote' },
  completed: { i: 5, label: 'Done' },
};

const STAGES = ['Made', '2nd', 'Debate', 'Put', 'Vote', 'Done'];

function speakerLabel(speaker) {
  if (!speaker) return '';
  if (typeof speaker === 'string') return speaker;
  return speaker.name || speaker.member || speaker.label || String(speaker);
}

/**
 * Lean heads-up dashboard for the chair while a secretary drives the full console.
 */
export default function ChairPromptView({
  theme = 'light',
  meetingSetup = {},
  chairScript = '',
  chairScriptRefining = false,
  chairScriptSource = 'template',
  currentMotion = null,
  motionTypes = {},
  currentAgendaIndex = 0,
  currentSpeaker = null,
  speakerQueue = [],
  speakerTime = 0,
  speakerTimeLimit = 0,
  meetingTimer = 0,
  pendingUCRequest = null,
  inElectionMode = false,
  electionScreen = 1,
  currentElection = null,
  electionNominees = [],
  currentCandidate = null,
  candidateSpeechQueue = [],
  onSignalReady = null,
  onSignalHold = null,
  onTakeControl = null,
  onReturnControl = null,
  onAddChairNote = null,
  onSendChairMessage = null,
  driverSeat = 'local',
  lastChairSignal = null,
  driverMessage = null,
  onDismissDriverMessage = null,
  electionBallotVotes = {},
  electionWriteInCandidates = [],
  isPopup = false,
  orgId = null,
}) {
  const [signalFlash, setSignalFlash] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteStatus, setNoteStatus] = useState(null);
  const [msgDraft, setMsgDraft] = useState('');
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgStatus, setMsgStatus] = useState(null);
  const quorumStatus = useMemo(() => getQuorumStatus(meetingSetup), [meetingSetup]);
  const currentAgendaItem = meetingSetup.orderOfBusiness?.[currentAgendaIndex] || null;
  const orgHome = orgId ? `/orgs/${orgId}` : '/orgs';
  const officeLabel = getElectionOfficeLabel(currentElection);

  const queue = inElectionMode
    ? (Array.isArray(candidateSpeechQueue) && candidateSpeechQueue.length
        ? candidateSpeechQueue
        : (electionNominees || []).filter(
            (n) => n.status === 'accepted' && !n.hasSpoken && n.id !== currentCandidate?.id
          )
      )
    : Array.isArray(speakerQueue)
      ? speakerQueue
      : [];

  const activeSpeaker = inElectionMode
    ? speakerLabel(currentCandidate) || speakerLabel(currentSpeaker)
    : speakerLabel(currentSpeaker);

  const nextStep = useMemo(
    () =>
      getChairNextStep({
        currentMotion,
        pendingUCRequest,
        currentAgendaItem,
        meetingSetup,
        currentSpeaker: activeSpeaker || currentSpeaker,
        quorumStatus,
        inElectionMode,
        electionScreen,
        currentElection,
        electionNominees,
        currentCandidate,
      }),
    [
      currentMotion,
      pendingUCRequest,
      currentAgendaItem,
      meetingSetup,
      currentSpeaker,
      activeSpeaker,
      quorumStatus,
      inElectionMode,
      electionScreen,
      currentElection,
      electionNominees,
      currentCandidate,
    ]
  );

  const stageIndex = currentMotion
    ? STAGE_SHORT[currentMotion.status]?.i ?? 0
    : -1;

  const motionLabel = currentMotion
    ? motionTypes[currentMotion.type]?.name || currentMotion.type
    : null;

  const card =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';

  const fireSignal = (kind, handler) => {
    setSignalFlash(kind);
    handler?.();
    window.setTimeout(() => setSignalFlash(null), 2000);
  };

  const timerUrgent = speakerTimeLimit > 0 && speakerTime > 0 && speakerTime <= 10;
  const timerWarn = speakerTimeLimit > 0 && speakerTime > 0 && speakerTime <= 30;
  const timerIdle = !activeSpeaker || speakerTime <= 0;

  const closePopup = () => {
    try {
      window.close();
    } catch {
      /* ignore */
    }
  };

  const submitNote = async () => {
    const text = noteDraft.trim();
    if (!text || typeof onAddChairNote !== 'function') return;
    setNoteBusy(true);
    setNoteStatus(null);
    try {
      await onAddChairNote(text);
      setNoteDraft('');
      setNoteStatus('Added to Activity');
      window.setTimeout(() => setNoteStatus(null), 2500);
    } catch (err) {
      setNoteStatus(err?.message || 'Could not add note');
    } finally {
      setNoteBusy(false);
    }
  };

  const submitSecretaryMessage = async () => {
    const text = msgDraft.trim();
    if (!text || typeof onSendChairMessage !== 'function') return;
    setMsgBusy(true);
    setMsgStatus(null);
    try {
      await onSendChairMessage(text);
      setMsgDraft('');
      setMsgStatus('Sent to secretary');
      window.setTimeout(() => setMsgStatus(null), 2500);
    } catch (err) {
      setMsgStatus(err?.message || 'Could not send message');
    } finally {
      setMsgBusy(false);
    }
  };

  const electionStageMeta = ELECTION_STAGES.find((s) => s.id === electionScreen) || ELECTION_STAGES[0];
  const acceptedNominees = (electionNominees || []).filter((n) => n.status === 'accepted');
  const pendingNominees = (electionNominees || []).filter(
    (n) => n.status !== 'accepted' && n.status !== 'declined'
  );
  const remainingSpeakers = (electionNominees || []).filter(
    (n) => n.status === 'accepted' && !n.hasSpoken && n.id !== currentCandidate?.id
  );
  const electionWinners = getElectionWinners({
    electionScreen,
    electionNominees,
    electionWriteInCandidates,
    electionBallotVotes,
    seatsToFill: currentElection?.seatsToFill || 1,
  });
  const showBreakdown = Boolean(currentElection?.showVoteBreakdown);

  return (
    <div
      className={`flex h-dvh max-h-dvh flex-col overflow-hidden p-3 sm:p-4 ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-pp-paper'
      }`}
      data-tour="chair-prompt-root"
    >
      {driverMessage?.body ? (
        <div
          className="fixed inset-x-0 top-0 z-50 flex justify-center p-3 sm:p-4"
          data-tour="driver-message-toast"
          role="alertdialog"
          aria-label="Message from secretary"
        >
          <div className="flex w-full max-w-xl items-start gap-3 rounded-2xl border border-pp-accent/40 bg-pp-accentSoft px-4 py-3 text-pp-ink shadow-lg">
            <MessageSquare className="mt-0.5 h-5 w-5 shrink-0 text-pp-accent" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-pp-accent">
                From the secretary
              </p>
              <p className="mt-1 text-base font-semibold leading-snug sm:text-lg">{driverMessage.body}</p>
              {driverMessage.at ? (
                <p className="mt-1 text-xs text-pp-muted">{driverMessage.at}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium underline"
              onClick={() => onDismissDriverMessage?.()}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col gap-3 overflow-hidden">
        <header
          className={`${card} flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-2xl px-4 py-3 shadow-pp`}
          data-tour="chair-prompt-header"
        >
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">
              Chair Dashboard
              {inElectionMode ? ' · Election' : ''}
            </p>
            <h1 className="truncate text-lg font-bold tracking-tight sm:text-xl">
              {meetingSetup.organizationName || 'Meeting'}
            </h1>
            <SignedInUserLine className="mt-0.5" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-pp-paper px-2.5 py-1.5 ring-1 ring-pp-line">
              <Timer size={16} className="text-pp-muted" />
              <span className="inline-block min-w-[5ch] font-mono tabular-nums">
                {formatTime(meetingTimer)}
              </span>
            </span>
            {meetingSetup.totalMembers > 0 && (
              <QuorumStatus quorumStatus={quorumStatus} size="normal" className="shrink-0" />
            )}
            <span className="hidden items-center gap-1.5 text-pp-muted sm:inline-flex">
              {inElectionMode ? <Vote size={14} /> : <Gavel size={14} />}
              <span className="max-w-[10rem] truncate">
                {inElectionMode ? officeLabel : currentAgendaItem?.title || 'Agenda'}
              </span>
            </span>
            <span className="hidden items-center gap-1.5 text-pp-muted md:inline-flex">
              <Users size={14} />
              {meetingSetup.presentMembers || 0}/{meetingSetup.totalMembers || 0}
            </span>
            <Link
              to={orgHome}
              className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line bg-pp-paper px-3 py-2 text-sm font-semibold text-pp-ink hover:bg-white"
              data-tour="chair-dashboard-leave"
            >
              <Home size={16} aria-hidden />
              Org home
            </Link>
            {isPopup ? (
              <button
                type="button"
                onClick={closePopup}
                className="inline-flex items-center gap-1.5 rounded-xl border border-pp-line bg-pp-paper px-3 py-2 text-sm font-semibold text-pp-ink hover:bg-white"
                data-tour="chair-dashboard-close"
              >
                <X size={16} aria-hidden />
                Close
              </button>
            ) : null}
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden lg:grid-cols-12">
          {/* Left: script, next step, actions, note */}
          <div className="flex min-h-0 flex-col gap-3 overflow-hidden lg:col-span-7">
            <section
              className={`${card} flex min-h-0 flex-[1.4] flex-col overflow-hidden rounded-2xl border-l-4 border-pp-accent p-4 shadow-pp sm:p-5`}
              data-tour="chair-prompt-script"
            >
              <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-pp-muted">
                Say this
                {chairScriptRefining
                  ? ' · Refining…'
                  : chairScriptSource === 'claude' || chairScriptSource === 'gateway'
                    ? ' · Suggested'
                    : ''}
              </p>
              <p className="mt-2 min-h-0 flex-1 overflow-y-auto text-xl font-semibold leading-snug sm:text-2xl lg:text-3xl">
                {chairScript || '—'}
              </p>
            </section>

            <section
              className={`${card} shrink-0 rounded-2xl p-4 shadow-pp`}
              data-tour="chair-prompt-next"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-pp-accent">Next step</p>
              <h2 className="mt-1 text-xl font-bold tracking-tight sm:text-2xl">{nextStep.title}</h2>
              <p className="mt-1 text-sm leading-snug text-pp-muted sm:text-base">{nextStep.detail}</p>
            </section>

            <div className="flex shrink-0 flex-wrap gap-2" data-tour="chair-prompt-actions">
              <button
                type="button"
                onClick={() => fireSignal('ready', onSignalReady)}
                className="rounded-xl bg-pp-sage px-4 py-3 text-sm font-semibold text-white hover:opacity-90"
              >
                Ready
              </button>
              <button
                type="button"
                onClick={() => fireSignal('hold', onSignalHold)}
                className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-950 hover:bg-amber-100"
              >
                Hold
              </button>
              {driverSeat === 'remote' ? (
                <button
                  type="button"
                  onClick={() => onReturnControl?.()}
                  className="rounded-xl bg-pp-accent px-4 py-3 text-sm font-semibold text-white hover:bg-pp-accentHover"
                >
                  Return control to secretary
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onTakeControl?.()}
                  className="rounded-xl bg-pp-accent px-4 py-3 text-sm font-semibold text-white hover:bg-pp-accentHover"
                >
                  Take control
                </button>
              )}
              {(signalFlash || lastChairSignal?.type === 'ready' || lastChairSignal?.type === 'hold') && (
                <p className="flex items-center text-sm font-medium text-pp-sage" role="status">
                  {signalFlash === 'ready' && 'Sent Ready to Driver'}
                  {signalFlash === 'hold' && 'Sent Hold to Driver'}
                  {!signalFlash && lastChairSignal?.type === 'ready' && 'Last signal: Ready'}
                  {!signalFlash && lastChairSignal?.type === 'hold' && 'Last signal: Hold'}
                </p>
              )}
            </div>

            <section
              className={`${card} shrink-0 rounded-2xl p-3 shadow-pp`}
              data-tour="chair-dashboard-note"
            >
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-pp-muted">
                <StickyNote size={14} aria-hidden />
                Note to Activity
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitNote();
                    }
                  }}
                  placeholder="Add a chair note to the Activity feed…"
                  maxLength={500}
                  className="min-w-[12rem] flex-1 rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-ink outline-none focus:ring-2 focus:ring-pp-accent/30"
                />
                <button
                  type="button"
                  disabled={noteBusy || !noteDraft.trim()}
                  onClick={() => void submitNote()}
                  className="rounded-lg bg-pp-accent px-3 py-2 text-sm font-semibold text-white hover:bg-pp-accentHover disabled:opacity-50"
                >
                  {noteBusy ? 'Adding…' : 'Add note'}
                </button>
              </div>
              {noteStatus ? (
                <p className="mt-1.5 text-xs font-medium text-pp-sage" role="status">
                  {noteStatus}
                </p>
              ) : null}
            </section>

            <section
              className={`${card} shrink-0 rounded-2xl p-3 shadow-pp`}
              data-tour="chair-dashboard-message"
            >
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-pp-muted">
                <MessageSquare size={14} aria-hidden />
                Message the secretary
              </p>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={msgDraft}
                  onChange={(e) => setMsgDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void submitSecretaryMessage();
                    }
                  }}
                  placeholder="Send a quick note to the Driver console…"
                  maxLength={280}
                  className="min-w-[12rem] flex-1 rounded-lg border border-pp-line bg-pp-paper px-3 py-2 text-sm text-pp-ink outline-none focus:ring-2 focus:ring-pp-accent/30"
                />
                <button
                  type="button"
                  disabled={msgBusy || !msgDraft.trim()}
                  onClick={() => void submitSecretaryMessage()}
                  className="rounded-lg bg-pp-sage px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {msgBusy ? 'Sending…' : 'Send'}
                </button>
              </div>
              {msgStatus ? (
                <p className="mt-1.5 text-xs font-medium text-pp-sage" role="status">
                  {msgStatus}
                </p>
              ) : null}
            </section>
          </div>

          {/* Right: floor, queue, motion/election */}
          <div className="flex min-h-0 flex-col gap-3 overflow-hidden lg:col-span-5">
            <section
              className={`${card} shrink-0 rounded-2xl p-4 shadow-pp`}
              data-tour="chair-prompt-speakers"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-pp-accent">Floor</p>
              <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-pp-muted">
                    {inElectionMode ? 'Candidate' : 'Speaking'}
                  </p>
                  <p className="truncate text-xl font-bold tracking-tight sm:text-2xl">
                    {activeSpeaker || (inElectionMode ? 'No candidate speaking' : 'No one has the floor')}
                  </p>
                </div>
                <div
                  className={`rounded-xl px-3 py-2 text-center ring-1 ${
                    timerIdle
                      ? 'bg-pp-paper text-pp-muted ring-pp-line'
                      : timerUrgent
                        ? 'bg-red-50 text-red-900 ring-red-200'
                        : timerWarn
                          ? 'bg-amber-50 text-amber-950 ring-amber-200'
                          : 'bg-pp-paper text-pp-ink ring-pp-line'
                  }`}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                    Speaker timer
                  </p>
                  <p className="font-mono text-2xl font-bold tabular-nums sm:text-3xl">
                    {formatTime(Math.max(0, speakerTime || 0))}
                  </p>
                  {speakerTimeLimit > 0 && (
                    <p className="text-xs text-pp-muted">Limit {formatTime(speakerTimeLimit)}</p>
                  )}
                </div>
              </div>

              <div className="mt-3 border-t border-pp-line/80 pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">
                  Up next {queue.length ? `(${queue.length})` : ''}
                </p>
                {queue.length === 0 ? (
                  <p className="mt-1 text-sm text-pp-muted">Queue is empty</p>
                ) : (
                  <ol className="mt-2 max-h-28 space-y-1.5 overflow-y-auto">
                    {queue.map((speaker, index) => (
                      <li
                        key={`${speakerLabel(speaker)}-${index}`}
                        className="flex items-center gap-2 text-sm font-medium sm:text-base"
                      >
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-pp-paper text-xs font-bold text-pp-muted ring-1 ring-pp-line">
                          {index + 1}
                        </span>
                        <span className="inline-flex min-w-0 items-center gap-1.5 truncate">
                          <Mic size={14} className="shrink-0 text-pp-muted" />
                          {speakerLabel(speaker)}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </section>

            {inElectionMode ? (
              <section
                className={`${card} flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl p-4 shadow-pp`}
                data-tour="chair-prompt-election"
              >
                <div className="shrink-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">
                    Election · {officeLabel}
                  </p>
                  <h3 className="mt-1 text-lg font-bold tracking-tight sm:text-xl">
                    {electionStageMeta.label}
                  </h3>
                </div>

                <div className="mt-3 min-h-0 flex-1 overflow-y-auto text-sm">
                  {electionScreen <= 1 && (
                    <p className="text-pp-muted">
                      Waiting for the Driver to configure and start the election for {officeLabel}.
                    </p>
                  )}
                  {electionScreen === 2 && (
                    <div className="space-y-2">
                      <p>
                        <span className="font-semibold">{acceptedNominees.length}</span> accepted
                        {pendingNominees.length > 0
                          ? ` · ${pendingNominees.length} awaiting response`
                          : ''}
                      </p>
                      {acceptedNominees.length > 0 ? (
                        <ul className="space-y-1">
                          {acceptedNominees.slice(0, 6).map((n) => (
                            <li key={n.id || n.name} className="font-medium">
                              {n.name}
                            </li>
                          ))}
                          {acceptedNominees.length > 6 ? (
                            <li className="text-pp-muted">+{acceptedNominees.length - 6} more</li>
                          ) : null}
                        </ul>
                      ) : (
                        <p className="text-pp-muted">No accepted nominees yet.</p>
                      )}
                    </div>
                  )}
                  {electionScreen === 3 && (
                    <div className="space-y-2">
                      <p>
                        <span className="text-pp-muted">Speaking now: </span>
                        <span className="font-semibold">
                          {currentCandidate?.name || '—'}
                        </span>
                      </p>
                      <p className="text-pp-muted">
                        Remaining: {remainingSpeakers.length || 0}
                      </p>
                      {remainingSpeakers.length > 0 ? (
                        <ul className="space-y-1">
                          {remainingSpeakers.slice(0, 5).map((n) => (
                            <li key={n.id || n.name}>{n.name}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  )}
                  {electionScreen === 4 && (
                    <p>
                      Conducting{' '}
                      <span className="font-semibold">
                        {currentElection?.secretBallot !== false ? 'secret ballot' : 'open'}
                      </span>{' '}
                      voting for {officeLabel}. The secretary tallies on the Driver console.
                    </p>
                  )}
                  {electionScreen >= 5 && (
                    <div className="space-y-2">
                      {electionWinners.length > 0 ? (
                        electionWinners.map((w) => (
                          <p key={w.name} className="font-semibold">
                            {w.name}
                            {showBreakdown ? ` · ${w.votes} vote${w.votes !== 1 ? 's' : ''}` : ''}
                          </p>
                        ))
                      ) : (
                        <p className="text-pp-muted">Results pending on the Driver console.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex w-full shrink-0 items-center gap-1 overflow-x-auto border-t border-pp-line/80 pt-3">
                  {ELECTION_STAGES.map((stage, index) => {
                    const active = electionScreen === stage.id;
                    const done = electionScreen > stage.id;
                    return (
                      <React.Fragment key={stage.id}>
                        <div className="flex shrink-0 items-center gap-1">
                          <div
                            className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                              done
                                ? 'border-pp-sage bg-pp-sageSoft text-pp-sage'
                                : active
                                  ? 'border-pp-accent bg-pp-accentSoft text-pp-accent'
                                  : 'border-pp-line bg-pp-paper text-pp-muted'
                            }`}
                          >
                            {done ? '✓' : stage.id}
                          </div>
                          <span
                            className={`text-xs font-semibold ${
                              done
                                ? 'text-pp-sage'
                                : active
                                  ? 'text-pp-accent'
                                  : 'text-pp-muted'
                            }`}
                          >
                            {stage.label}
                          </span>
                        </div>
                        {index < ELECTION_STAGES.length - 1 && (
                          <div
                            className={`mx-0.5 h-0.5 min-w-[0.35rem] flex-1 ${
                              done ? 'bg-pp-sage' : 'bg-pp-line'
                            }`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </section>
            ) : currentMotion ? (
              <section
                className={`${card} flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl p-4 shadow-pp`}
                data-tour="chair-prompt-progress"
              >
                <p className="shrink-0 text-xs font-semibold uppercase tracking-wide text-pp-muted">
                  {motionLabel || 'Motion'}
                </p>
                <p className="mt-1 line-clamp-4 text-base font-semibold leading-snug sm:text-lg">
                  “{currentMotion.text}”
                </p>
                {currentMotion.member && (
                  <p className="mt-1 shrink-0 text-sm text-pp-muted">
                    {currentMotion.member}
                    {currentMotion.secondedBy ? ` · Second: ${currentMotion.secondedBy}` : ''}
                  </p>
                )}
                <div className="mt-auto flex w-full items-center gap-1 overflow-x-auto pt-3">
                  {STAGES.map((label, index) => (
                    <React.Fragment key={label}>
                      <div className="flex shrink-0 items-center gap-1">
                        <div
                          className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                            index < stageIndex
                              ? 'border-pp-sage bg-pp-sageSoft text-pp-sage'
                              : index === stageIndex
                                ? 'border-pp-accent bg-pp-accentSoft text-pp-accent'
                                : 'border-pp-line bg-pp-paper text-pp-muted'
                          }`}
                        >
                          {index < stageIndex ? '✓' : index + 1}
                        </div>
                        <span
                          className={`text-xs font-semibold ${
                            index < stageIndex
                              ? 'text-pp-sage'
                              : index === stageIndex
                                ? 'text-pp-accent'
                                : 'text-pp-muted'
                          }`}
                        >
                          {label}
                        </span>
                      </div>
                      {index < STAGES.length - 1 && (
                        <div
                          className={`mx-0.5 h-0.5 min-w-[0.35rem] flex-1 ${
                            index < stageIndex ? 'bg-pp-sage' : 'bg-pp-line'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </section>
            ) : (
              <section className={`${card} flex-1 rounded-2xl p-4 shadow-pp`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-pp-muted">Motion</p>
                <p className="mt-2 text-base text-pp-muted">No motion on the floor</p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
